import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api, { inr } from "@/api";
import { Reveal, MaskedLines, Marquee, Magnetic, Overline, EASE } from "@/components/Motion";
import { RedDot } from "@/components/Logo";
import { useSettings, usePage } from "@/settings";
import HeroLogo from "@/components/HeroLogo";

function Hero() {
  const ref = useRef(null);
  const s = useSettings();
  const pg = usePage("home");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden" style={{ minHeight: "100svh" }}>
      <motion.div style={{ y: yText, opacity }} className="px-6 md:px-12 max-w-7xl mx-auto w-full pt-28 pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-ink/50 mb-8"
        >
          {s.hero_overline}
        </motion.p>
        <div className="overflow-hidden w-full flex justify-center">
          <HeroLogo />
        </div>
        <MaskedLines
          as="p"
          delay={0.7}
          className="mt-10 max-w-xl"
          lineClassName="font-display italic text-2xl md:text-3xl text-ink/80 leading-snug"
          lines={[s.hero_line_1, s.hero_line_2]}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Magnetic>
            <Link
              to={pg.hero_primary_to || "/work"}
              data-testid="hero-explore-work-btn"
              className="group inline-flex items-center gap-3 bg-ink text-paper px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors duration-300"
            >
              {pg.hero_primary_label || "Explore Work"} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Magnetic>
          <Magnetic>
            <Link
              to={pg.hero_secondary_to || "/contact"}
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-3 border border-ink px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:border-brand hover:text-brand transition-colors duration-300"
            >
              {pg.hero_secondary_label || "Start a Project"}
            </Link>
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40"
      >
        <span className="w-px h-10 bg-ink/30 overflow-hidden relative">
          <motion.span
            className="absolute inset-x-0 top-0 h-4 bg-brand"
            animate={{ y: [0, 40] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          />
        </span>
        Scroll
      </motion.div>
    </section>
  );
}

function SelectedArchitecture({ projects }) {
  const pg = usePage("home");
  if (!projects.length || pg.show_architecture === false) return null;
  return (
    <section data-testid="home-architecture" className="px-6 md:px-12 py-24 lg:py-32 max-w-7xl mx-auto">
      <Reveal>
        <div className="flex items-end justify-between mb-14">
          <div>
            <Overline>Selected Architecture</Overline>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">{pg.heading_architecture || "Built work"}</h2>
          </div>
          <Link to="/work/architecture" data-testid="view-architecture-link" className="link-line hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
            View Architecture <ArrowUpRight size={14} />
          </Link>
        </div>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {projects.slice(0, 3).map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1} className={i === 0 ? "md:col-span-8" : i === 1 ? "md:col-span-4" : "md:col-span-12"}>
            <Link to={`/work/architecture/${p.id}`} data-testid={`arch-card-${p.id}`} className="group block">
              <div className={`frame-clip spotlight ${i === 2 ? "aspect-[21/9]" : "aspect-[4/3]"}`}>
                <img src={p.cover} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="flex items-baseline justify-between mt-4 border-t border-ink/15 pt-3">
                <h3 className="font-display text-xl md:text-2xl group-hover:text-brand transition-colors">{p.title}</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">
                  {p.location} · {p.year} · {p.category}
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function SelectedArt({ artworks }) {
  const pg = usePage("home");
  if (!artworks.length || pg.show_art === false) return null;
  return (
    <section data-testid="home-art" className="bg-ink text-paper px-6 md:px-12 py-24 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex items-end justify-between mb-14">
            <div>
              <Overline dark>Selected Art</Overline>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">{pg.heading_art || "Gallery"}</h2>
            </div>
            <Link to="/work/art" data-testid="explore-art-link" className="link-line hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
              Explore Art <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-6 items-end">
          {artworks.slice(0, 4).map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08} className={["col-span-1 md:col-span-4", "col-span-1 md:col-span-3 md:mb-16", "col-span-1 md:col-span-3", "col-span-1 md:col-span-2 md:mb-24"][i]}>
              <Link to={`/work/art/${a.id}`} data-testid={`art-card-${a.id}`} className="group block">
                <div className="frame-clip spotlight">
                  <img src={a.image} alt={a.title} className="w-full h-auto object-cover" loading="lazy" />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <RedDot className="w-1.5 h-1.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <h3 className="font-display text-base md:text-lg text-paper/90">{a.title}</h3>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/40 mt-1">{a.category} · {a.year}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutStrip() {
  const s = useSettings();
  const pg = usePage("home");
  if (pg.show_about === false) return null;
  return (
    <section data-testid="home-about" className="px-6 md:px-12 py-24 lg:py-32 max-w-7xl mx-auto">
      <Reveal>
        <Overline>About IKSHA</Overline>
      </Reveal>
      <MaskedLines
        as="h2"
        className="mt-8 max-w-4xl"
        lineClassName="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight"
        lines={s.home_about_lines.split("\n").filter(Boolean)}
      />
      <Reveal delay={0.3} className="mt-10">
        <Link to="/about" data-testid="home-about-link" className="link-line inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
          Our philosophy <ArrowUpRight size={14} />
        </Link>
      </Reveal>
    </section>
  );
}

const SERVICES = [
  "Architecture Consultation", "Architectural Design", "Interior Design",
  "Sustainable Design & Material Consultation", "3D Visualisation",
  "Art Commissions", "Portraits", "Wall Art",
];

function ServicesStrip() {
  const pg = usePage("home");
  if (pg.show_services === false) return null;
  return (
    <section data-testid="home-services" className="bg-linen border-y border-ink/10 px-6 md:px-12 py-24 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Overline>Services</Overline>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4 mb-14">{pg.heading_services || "What the studio does"}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
          {SERVICES.map((s, i) => (
            <Reveal key={s} delay={i * 0.05} y={20}>
              <Link
                to="/services"
                data-testid={`home-service-${i}`}
                className="group flex items-center justify-between py-5 border-b border-ink/15 hover:border-brand transition-colors"
              >
                <span className="flex items-center gap-4">
                  <span className="font-mono text-[10px] text-ink/40">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-lg md:text-2xl group-hover:text-brand transition-colors">{s}</span>
                </span>
                <RedDot className="w-2 h-2 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorkshopHighlight({ workshop }) {
  const pg = usePage("home");
  if (!workshop || pg.show_workshop === false) return null;
  return (
    <section data-testid="home-workshop" className="px-6 md:px-12 py-24 lg:py-32 max-w-7xl mx-auto">
      <Reveal>
        <Overline>Upcoming Workshop</Overline>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10 items-center">
        <Reveal className="lg:col-span-7">
          <div className="frame-clip spotlight aspect-[16/10] border border-ink/10">
            <img src={workshop.cover} alt={workshop.title} className="w-full h-full object-cover" loading="lazy" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand flex items-center gap-2">
            <RedDot className="w-2 h-2 animate-pulse-dot" /> Registration open
          </p>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-4">{workshop.title}</h3>
          <p className="mt-4 text-ink/60 leading-relaxed">{workshop.intro}</p>
          <dl className="mt-6 grid grid-cols-2 gap-4 font-mono text-xs uppercase tracking-[0.15em] text-ink/70">
            <div><dt className="text-ink/40">Date</dt><dd className="mt-1">{workshop.dates}</dd></div>
            <div><dt className="text-ink/40">Location</dt><dd className="mt-1">{workshop.location}</dd></div>
            <div><dt className="text-ink/40">Duration</dt><dd className="mt-1">{workshop.duration}</dd></div>
            <div><dt className="text-ink/40">Fee</dt><dd className="mt-1">{inr(workshop.fee)}</dd></div>
          </dl>
          <Magnetic>
            <Link
              to={`/workshops/${workshop.id}`}
              data-testid="register-workshop-btn"
              className="mt-8 inline-flex items-center gap-3 bg-brand text-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors"
            >
              Register Now <ArrowRight size={14} />
            </Link>
          </Magnetic>
        </Reveal>
      </div>
    </section>
  );
}

function ShopPreview({ products }) {
  const pg = usePage("home");
  if (!products.length || pg.show_shop === false) return null;
  return (
    <section data-testid="home-shop" className="bg-paper px-6 md:px-12 py-24 lg:py-32 border-t border-ink/10">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex items-end justify-between mb-14">
            <div>
              <Overline>IKSHA Shop</Overline>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4">{pg.heading_shop || "Objects & editions"}</h2>
            </div>
            <Link to="/shop" data-testid="visit-shop-link" className="link-line hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
              Visit Shop <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {products.slice(0, 4).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link to={`/shop/${p.id}`} data-testid={`product-card-${p.id}`} className="group block">
                <div className="frame-clip spotlight aspect-[4/5] bg-linen border border-ink/10 p-4">
                  <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="mt-3 flex justify-between items-baseline gap-2">
                  <h3 className="font-display text-base group-hover:text-brand transition-colors leading-snug">{p.name}</h3>
                  <span className="font-mono text-xs text-ink/60 shrink-0">{inr(p.price)}</span>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 mt-1">{p.status}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  const pg = usePage("home");
  if (pg.show_cta === false) return null;
  return (
    <section data-testid="home-cta" className="bg-terra text-paper px-6 md:px-12 py-28 lg:py-40">
      <div className="max-w-7xl mx-auto">
        <MaskedLines
          as="h2"
          className="max-w-5xl"
          lineClassName="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05]"
          lines={[pg.cta_line_1 || "Have a project, artwork", pg.cta_line_2 || "or collaboration in mind?"]}
        />
        <Reveal delay={0.35} className="mt-12 flex flex-wrap gap-4">
          <Magnetic>
            <Link to="/contact?type=Architecture" data-testid="cta-start-project-btn"
              className="inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors">
              {pg.cta_primary_label || "Start a Project"} <ArrowRight size={14} />
            </Link>
          </Magnetic>
          <Magnetic>
            <Link to="/contact?type=Artwork Commission" data-testid="cta-commission-btn"
              className="inline-flex items-center gap-3 border border-paper/70 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-paper hover:text-ink transition-colors">
              {pg.cta_secondary_label || "Commission an Artwork"}
            </Link>
          </Magnetic>
          <Magnetic>
            <Link to="/contact" data-testid="cta-contact-btn"
              className="inline-flex items-center gap-3 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] underline underline-offset-8 hover:text-ink transition-colors">
              {pg.cta_tertiary_label || "Contact IKSHA"}
            </Link>
          </Magnetic>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState({ projects: [], artworks: [], workshop: null, products: [] });
  const pg = usePage("home");

  useEffect(() => {
    Promise.all([
      api.get("/projects"),
      api.get("/artworks"),
      api.get("/workshops", { params: { status: "upcoming" } }),
      api.get("/products"),
    ]).then(([p, a, w, pr]) => {
      const pick = (list, ids) =>
        ids && ids.length ? ids.map((i) => list.find((x) => x.id === i)).filter(Boolean) : list.filter((x) => x.featured);
      setData({
        projects: pick(p.data, pg.featured_projects),
        artworks: pick(a.data, pg.featured_artworks),
        workshop: w.data.find((x) => x.id === pg.featured_workshop) || w.data[0] || null,
        products: pick(pr.data, pg.featured_products),
      });
    }).catch(() => {});
  }, [pg]);

  return (
    <main>
      <Hero />
      <Marquee items={(pg.marquee_items || "Art, Architecture, Material, Craft, Making, Learning").split(",").map((s) => s.trim()).filter(Boolean)} />
      <SelectedArchitecture projects={data.projects} />
      <SelectedArt artworks={data.artworks} />
      <AboutStrip />
      <ServicesStrip />
      <WorkshopHighlight workshop={data.workshop} />
      <ShopPreview products={data.products} />
      <ClosingCTA />
    </main>
  );
}
